import { FieldArray, Field, FieldArrayRenderProps } from 'formik';
import { Box } from '@origin-digital/layout';
import React from 'react';
import { connect } from 'react-redux';
import DisqualificationProvider from '../../../components/DisqualificationProvider';
import { DisqualificationContext } from '../../../components/DisqualificationProvider/disqualification-context';
import {
  RadioGroup,
  AddressField,
  HiddenField,
  RadioImageGroupField,
  ManualAddress
} from '../../../components/Form/Fields';
import DisqualificationAlert from '../../../components/DisqualificationAlert';
import FormRow from '../../../components/Form/Layout/FormRow';

import analytics from '../../../analytics';
import { CHECK_SERVICEABILITY } from '../../../graphql/queries';
import { isFieldTouched } from '../../../utils/formHelpers';

import { updateCustomerDetail } from '../../../redux/actions/customer';
import { checkIfPostcodeServiceable } from '../../../redux/thunks';

import { StepOneProps, StepOneState } from './StepOne.types';
import { RegulatorySignPostSA } from '../../../components/Regulatory';
// @ts-ignore
import { stepOne } from '../content.json';

export class StepOne extends React.Component<StepOneProps, StepOneState> {
  componentDidMount() {
    analytics.pageLoad('step1');
  }

  render(): JSX.Element {
    const {
      validating,
      customerAddress,
      productType,
      disqualifiedFields,
      isInitialValid,
      hasManualAddress,
      updateCustomerDetail,
      checkIfPostcodeServiceable
    } = this.props;

    return (
      <DisqualificationProvider disqualificationMessages={stepOne.content.disqualification}>
        <DisqualificationContext.Consumer>
          {context => (
            <React.Fragment>
              <FormRow
                content={stepOne.content.productType}
                byline={stepOne.content.productType.byline}
              >
                <FieldArray
                  name={stepOne.fields.productType.name}
                  render={(helpers: FieldArrayRenderProps) => (
                    <RadioGroup
                      name={stepOne.fields.productType.name}
                      options={stepOne.fields.productType.options}
                      helpers={helpers}
                      changeHandler={updateCustomerDetail}
                    />
                  )}
                />
              </FormRow>
              <FormRow
                content={stepOne.content.propertyAddress}
                byline={
                  productType !== 'SOLAR'
                    ? stepOne.content.propertyAddress.batteryByline
                    : stepOne.content.propertyAddress.byline
                }
              >
                <Field
                  name={stepOne.fields.address.name}
                  component={AddressField}
                  label={stepOne.fields.address.label}
                  productType={productType}
                  customerAddress={customerAddress}
                  isInitialValid={isInitialValid}
                  isDisqualified={disqualifiedFields.includes(stepOne.fields.hiddenPostcode.name)}
                  hasManualAddress={hasManualAddress}
                  manualAddressContent={stepOne}
                  manualAddressPlaceholder={stepOne.content.address.manualAddressPlaceholder}
                  graphQuery={CHECK_SERVICEABILITY}
                  validating={validating}
                  changeHandler={updateCustomerDetail}
                  addressCheckHandler={checkIfPostcodeServiceable}
                />
                <ManualAddress
                  formData={stepOne}
                  productType={productType}
                  changeHandler={updateCustomerDetail}
                  isInitialValid={isInitialValid}
                  hasManualAddress={hasManualAddress}
                  addressCheckHandler={checkIfPostcodeServiceable}
                  isDisqualified={disqualifiedFields.includes(stepOne.fields.hiddenPostcode.name)}
                  validating={validating}
                  graphQuery={CHECK_SERVICEABILITY}
                />
                {customerAddress.qasAddress && customerAddress.qasAddress.state === 'SA' && (
                  <Box marginTop={-2}>
                    <RegulatorySignPostSA />
                  </Box>
                )}
                <Field
                  name={stepOne.fields.hiddenPostcode.name}
                  render={(helpers: FieldArrayRenderProps) => {
                    const touched: boolean = isFieldTouched(helpers.form, name);
                    return (
                      <React.Fragment>
                        <HiddenField
                          name={stepOne.fields.hiddenPostcode.name}
                          touched={touched}
                          helpers={helpers}
                          disqualificationHandler={context.disqualificationHandler}
                        />
                        <Box marginTop={-3}>
                          <DisqualificationAlert
                            name={stepOne.fields.hiddenPostcode.name}
                            heading={stepOne.content.disqualification.heading}
                            message={stepOne.content.disqualification.hiddenPostcode}
                          />
                        </Box>
                      </React.Fragment>
                    );
                  }}
                />
              </FormRow>

              {productType !== 'BATTERY' && (
                <React.Fragment>
                  <FormRow
                    content={stepOne.content.propertyType}
                    byline={stepOne.content.propertyType.byline}
                  >
                    <FieldArray
                      name={stepOne.fields.propertyType.name}
                      render={(helpers: FieldArrayRenderProps) => {
                        const touched: boolean = isFieldTouched(helpers.form, name);

                        return (
                          <React.Fragment>
                            <RadioImageGroupField
                              name={stepOne.fields.propertyType.name}
                              touched={touched}
                              options={stepOne.fields.propertyType.options}
                              helpers={helpers}
                              changeHandler={updateCustomerDetail}
                              isDisqualified={disqualifiedFields.includes(
                                stepOne.fields.propertyType.name
                              )}
                              disqualificationHandler={context.disqualificationHandler}
                            />
                            <DisqualificationAlert
                              name={stepOne.fields.propertyType.name}
                              heading={stepOne.content.disqualification.heading}
                              message={stepOne.content.disqualification.propertyType}
                            />
                          </React.Fragment>
                        );
                      }}
                    />
                  </FormRow>

                  <FormRow
                    content={stepOne.content.roofType}
                    byline={stepOne.content.roofType.byline}
                  >
                    <FieldArray
                      name={stepOne.fields.roofType.name}
                      render={(helpers: FieldArrayRenderProps) => {
                        const touched: boolean = isFieldTouched(helpers.form, name);

                        return (
                          <React.Fragment>
                            <RadioImageGroupField
                              name={stepOne.fields.roofType.name}
                              touched={touched}
                              options={stepOne.fields.roofType.options}
                              helpers={helpers}
                              changeHandler={updateCustomerDetail}
                              isDisqualified={disqualifiedFields.includes(
                                stepOne.fields.roofType.name
                              )}
                              disqualificationHandler={context.disqualificationHandler}
                            />
                            <DisqualificationAlert
                              name={stepOne.fields.roofType.name}
                              heading={stepOne.content.disqualification.heading}
                              message={stepOne.content.disqualification.roofType}
                            />
                          </React.Fragment>
                        );
                      }}
                    />
                  </FormRow>
                </React.Fragment>
              )}
            </React.Fragment>
          )}
        </DisqualificationContext.Consumer>
      </DisqualificationProvider>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    propertyType: state.customer.propertyType,
    productType: state.customer.productType,
    hasManualAddress: state.customer.hasManualAddress ? state.customer.hasManualAddress : false,
    customerAddress: {
      label: state.customer.address,
      qasAddress: {
        unit: state.customer.unit ? state.customer.unit : '',
        streetNumber: state.customer.streetNumber ? state.customer.streetNumber : '',
        streetName: state.customer.streetName ? state.customer.streetName : '',
        streetType: state.customer.streetType ? state.customer.streetType : '',
        streetNameOnly: state.customer.streetNameOnly ? state.customer.streetNameOnly : '',
        suburb: state.customer.suburb ? state.customer.suburb : '',
        city: state.customer.city ? state.customer.city : '',
        state: state.customer.state ? state.customer.state : '',
        postcode: state.customer.postcode ? state.customer.postcode : '',
        hiddenPostcode: state.customer.hiddenPostcode ? state.customer.hiddenPostcode : 'true',
        label: state.customer.address ? state.customer.address : ''
      }
    },
    disqualifiedFields: state.disqualification.disqualifiedFields,
    validating: state.request.validating
  };
};

export default connect(
  mapStateToProps,
  {
    updateCustomerDetail,
    checkIfPostcodeServiceable
  }
)(StepOne);
